import { ENDPOINTS } from 'config/endpoints';
import {
  InfiniteData,
  QueryClient,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import CronoError from 'crono-fe-common/types/error';
import client from 'utils/clients/client';
import { Request } from 'crono-fe-common/types/request';
import { Response, ResponsePaginated } from 'crono-fe-common/types/response';
import { Prospect } from 'crono-fe-common/types/prospect';
import { UpdateProspect } from 'crono-fe-common/types/DTO/updateProspect';
import { useCrmPushActions } from 'context/crmPush';

export function createEditProspectRequest(updateProspect: UpdateProspect) {
  const request: Request = {
    url: `${ENDPOINTS.prospect.main}`,
    config: {
      method: 'patch',
      data: { data: updateProspect },
    },
  };
  return request;
}

export default function useEditProspect() {
  const queryClient = useQueryClient();
  const { resumePolling } = useCrmPushActions();

  const editProspect = (updateProspect: UpdateProspect) => {
    const request = createEditProspectRequest(updateProspect);
    return client(request);
  };

  return useMutation<Response<null>, CronoError, UpdateProspect>(editProspect, {
    onSuccess: (_r, variables) => {
      if (isExternalValuesOnlyUpdate(variables)) {
        patchProspectExternalValuesInCache(queryClient, variables);
      } else {
        invalidateEditProspectQueries(queryClient);
      }
    },
    onSettled: (_r, _e, variables) => {
      const externalValuesProvided =
        variables?.externalValues &&
        Object.keys(variables.externalValues).length > 0;

      if (externalValuesProvided || variables.actualStatus) {
        resumePolling();
      }
    },
    meta: {
      errorMessage: 'Failed to update contact',
      successMessage: 'Contact updated successfully',
    },
  });
}

// True only when the patch contains exclusively externalValues — no name,
// status, accountId, or any other field that would affect tasks/strategy views.
function isExternalValuesOnlyUpdate(variables: UpdateProspect): boolean {
  const { prospectId: _id, externalValues, ...rest } = variables;
  if (!externalValues || Object.keys(externalValues).length === 0) return false;
  return Object.values(rest).every((v) => v === undefined || v === null);
}

// Patches the affected ExternalValue entries directly in the
// useSearchProspects, useSearchProspectsInfiniteScroll and useGetProspect
// caches without triggering a refetch. Unchanged ExternalValue objects keep
// the same reference so memoized components that depend on them don't
// re-render.
// A property the contact has no value for yet is simply absent from the cached
// list, so it gets appended rather than patched in place.
function patchProspectExternalValuesInCache(
  queryClient: QueryClient,
  variables: UpdateProspect,
): void {
  const { prospectId, externalValues } = variables;
  if (!externalValues) return;

  const patchProspect = (prospect: Prospect): Prospect => {
    if (prospect.objectId !== prospectId) return prospect;

    const currentValues = prospect.externalValues ?? [];
    const existingPropertyIds = new Set(
      currentValues.map((ev) => ev.externalPropertyId),
    );

    return {
      ...prospect,
      externalValues: [
        ...currentValues.map((ev) => {
          const newValue = externalValues[ev.externalPropertyId];
          return newValue !== undefined ? { ...ev, value: newValue } : ev;
        }),
        ...Object.entries(externalValues)
          .filter(([id]) => !existingPropertyIds.has(Number(id)))
          .map(([id, value]) => ({
            externalPropertyId: Number(id),
            value: value ?? null,
          })),
      ],
    };
  };

  const patchPage = (
    page: ResponsePaginated<Prospect[]>,
  ): ResponsePaginated<Prospect[]> => {
    const prospects = page.data?.data;
    if (!prospects?.some((p) => p.objectId === prospectId)) return page;

    return {
      ...page,
      data: {
        ...page.data,
        data: prospects.map(patchProspect),
      },
    } as ResponsePaginated<Prospect[]>;
  };

  queryClient.setQueriesData<
    InfiniteData<ResponsePaginated<Prospect[]>> | ResponsePaginated<Prospect[]>
  >(
    {
      predicate: (query) =>
        Array.isArray(query.queryKey) &&
        query.queryKey[0] === ENDPOINTS.prospect.search.main,
    },
    (old) => {
      if (!old) return old;

      // useSearchProspectsInfiniteScroll cache
      if ('pages' in old) {
        let changed = false;
        const newPages = old.pages.map((page) => {
          const newPage = patchPage(page);
          if (newPage !== page) changed = true;
          return newPage;
        });

        if (!changed) return old;

        return { ...old, pages: newPages };
      }

      // plain useSearchProspects cache
      return patchPage(old);
    },
  );

  queryClient.setQueriesData<Response<Prospect | null>>(
    {
      predicate: (query) =>
        Array.isArray(query.queryKey) &&
        query.queryKey[0] === ENDPOINTS.prospect.main &&
        query.queryKey[1] === prospectId,
    },
    (old) => {
      if (!old?.data?.data) return old;

      return {
        ...old,
        data: { ...old.data, data: patchProspect(old.data.data) },
      } as Response<Prospect | null>;
    },
  );
}

export async function invalidateEditProspectQueries(queryClient: QueryClient) {
  await Promise.all([
    queryClient.invalidateQueries({
      predicate: (query) =>
        query.queryKey.toString().startsWith(ENDPOINTS.prospect.main) ||
        query.queryKey.toString().startsWith(ENDPOINTS.prospect.search.main),
    }),
    queryClient.invalidateQueries([ENDPOINTS.prospect.main]),
    queryClient.invalidateQueries([ENDPOINTS.account.search.main]),
    queryClient.invalidateQueries([ENDPOINTS.task.search]),
    queryClient.invalidateQueries([ENDPOINTS.task.main]),

    queryClient.invalidateQueries([ENDPOINTS.strategy.search]),
    queryClient.invalidateQueries([ENDPOINTS.strategy.prospect]),
    queryClient.invalidateQueries([ENDPOINTS.prospect.search.strategy]),

    queryClient.invalidateQueries([ENDPOINTS.strategy.details]),
  ]);
}
